import { walletApiSlice } from '../apiSlice';
import type { IWalletPagedResult } from './types';

/**
 * Admin wallet management — search, inspect, freeze, adjust and reconcile any user's wallet.
 *
 * <p>Distinct from `wallets.api.ts`, which is the SELF-service surface (`/wallets/...`, the
 * signed-in user's own wallet). Everything here sits under `/admin/wallets/...`, acts on other
 * people's money, and is audited: freeze, unfreeze and adjust all take a reason that is stored,
 * and manual credit records the deposit it answers to.</p>
 *
 * <p>Ids in this service are <b>GUIDs</b>, including `adminUserId` — that is the wallet-service
 * user id, NOT api.core's integer `UserId` claim. Resolve it from the signed-in admin's email via
 * `useGetUserByEmailMutation` before calling anything that needs it; sending the core integer
 * binds to a default and attributes the action to nobody.</p>
 */

export const WALLET_STATUS = { ACTIVE: 1, FROZEN: 2, SUSPENDED: 3, CLOSED: 4 } as const;
export type IWalletStatus = (typeof WALLET_STATUS)[keyof typeof WALLET_STATUS];

export const WALLET_STATUS_LABEL: Record<IWalletStatus, string> = {
	[WALLET_STATUS.ACTIVE]: 'Active',
	[WALLET_STATUS.FROZEN]: 'Frozen',
	[WALLET_STATUS.SUSPENDED]: 'Suspended',
	[WALLET_STATUS.CLOSED]: 'Closed',
};

/** `CREDIT` adds to the wallet, `DEBIT` deducts. There is no signed-amount form — the amount is always positive. */
export const ADJUSTMENT_DIRECTION = { CREDIT: 1, DEBIT: 2 } as const;
export type IAdjustmentDirection = (typeof ADJUSTMENT_DIRECTION)[keyof typeof ADJUSTMENT_DIRECTION];

export interface IAdminWalletSearchRequest {
	searchTerm?: string;
	status?: IWalletStatus;
	currency?: string;
	minBalance?: number;
	maxBalance?: number | null;
	fromDate?: string;
	toDate?: string | null;
	/** Server sort expression, e.g. `'CreatedAt desc'`. Accepted tokens are undocumented. */
	orderBy?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface IAdminWalletListItem {
	id: string;
	tenantId: string;
	userId: string;
	email: string;
	currency: string;
	status: IWalletStatus;
	statusReason: string | null;
	/** Total ledger book balance. */
	currentBalance: number;
	/** Spendable. */
	availableBalance: number;
	/** Reserved / held. */
	pendingBalance: number;
	bucketCount: number;
	createdAt: string;
	updatedAt: string;
}

export interface IAdminCreditBucketDetail {
	id: string;
	creditBucketTypeId: string;
	creditType: string;
	priority: number;
	currentBalance: number;
	availableBalance: number;
	pendingBalance: number;
	expiresAt: string | null;
	isExpired: boolean;
}

/**
 * A transaction as the ADMIN detail response carries it.
 *
 * <p>Deliberately not `IWalletTransaction` from `wallets.api.ts`: this shape is narrower and its
 * fields disagree — `transactionType` is a STRING here (`'Credit'`) and a number there, and there
 * is no `walletId`, `bucketId`, `source` or `balanceBefore`. Reusing that interface would typecheck
 * against a response that cannot satisfy it.</p>
 */
export interface IAdminWalletTransactionItem {
	id: string;
	transactionType: string;
	amount: number;
	balanceAfter: number;
	description: string;
	reference: string;
	createdAt: string;
}

export interface IAdminWalletDetailsResult {
	id: string;
	tenantId: string;
	userId: string;
	email: string;
	currency: string;
	status: IWalletStatus;
	statusReason: string | null;
	statusUpdatedAt: string | null;
	currentBalance: number;
	availableBalance: number;
	pendingBalance: number;
	creditBuckets: IAdminCreditBucketDetail[];
	recentTransactions: IAdminWalletTransactionItem[];
	/** The balance the ledger sums to, independent of the stored figure. */
	calculatedLedgerBalance: number;
	/** False when the stored balance and the ledger disagree — surface it, never silently prefer one. */
	isLedgerConsistent: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface IAdminWalletStatusRequest {
	walletId: string;
	/** Stored on the wallet as `statusReason` and shown back to the next admin. Required. */
	reason: string;
	/** wallet-service user GUID of the acting admin — see the file header. */
	adminUserId: string;
}

/** Freeze and unfreeze both answer with this, NOT with the updated wallet. */
export interface IAdminWalletStatusResult {
	message: string;
	walletId: string;
}

export interface IAdjustWalletBalanceRequest {
	walletId: string;
	direction: IAdjustmentDirection;
	/** Always positive — `direction` carries the sign. */
	amount: number;
	/** Optional. Omitted, the service targets the highest-priority bucket. */
	creditBucketTypeId?: string | null;
	creditType?: string | null;
	reason: string;
	/** The ticket or case this adjustment answers to. Free text. */
	externalReference?: string | null;
	adminUserId: string;
}

export interface IAdjustWalletBalanceResult {
	transactionId: string;
	walletId: string;
	direction: IAdjustmentDirection;
	amount: number;
	newCurrentBalance: number;
	newAvailableBalance: number;
	reference: string;
	reason: string;
	adjustedAt: string;
}

/**
 * How an over-the-counter deposit reached the company account. A STRING enum on the wire —
 * unlike `AdjustmentDirection` and `WalletStatus`, which are integers.
 */
export const DEPOSIT_CHANNEL = { CASH: 'Cash', BANK_TRANSFER: 'BankTransfer' } as const;
export type IDepositChannel = (typeof DEPOSIT_CHANNEL)[keyof typeof DEPOSIT_CHANNEL];

export const DEPOSIT_CHANNEL_LABEL: Record<IDepositChannel, string> = {
	[DEPOSIT_CHANNEL.CASH]: 'Cash',
	[DEPOSIT_CHANNEL.BANK_TRANSFER]: 'Bank transfer',
};

/**
 * Credit a wallet for money received over the counter.
 *
 * <p>Every key is required on the wire, including the nullable ones — send `null`, never omit
 * and never `""`. Only `Active` wallets accept it; frozen ones are rejected.</p>
 */
export interface IManualWalletCreditRequest {
	walletId: string;
	/** NAIRA received, whole units. The service converts to credits at its current rate and lands them in `GENERAL`. */
	amount: number;
	depositChannel: IDepositChannel;
	depositorName: string;
	/** Teller / slip / transfer reference. Rejected if already used on ANY wallet — the double-credit guard. */
	depositReference: string;
	companyAccountReference: string | null;
	depositDate: string | null;
	reason: string | null;
	/** wallet-service user GUID of the acting admin — see the file header. */
	adminUserId: string;
}

export interface IManualWalletCreditResult {
	transactionId: string;
	walletId: string;
	/** Naira, as sent. */
	amount: number;
	/** Credits, after conversion. */
	newCurrentBalance: number;
	newAvailableBalance: number;
	reference: string;
	depositChannel: IDepositChannel;
	depositorName: string | null;
	depositReference: string;
	companyAccountReference: string | null;
	depositDate: string | null;
	reason: string | null;
	creditedAt: string;
}

export interface IWalletReconciliationResult {
	walletId: string;
	userId: string;
	email: string;
	currency: string;
	isConsistent: boolean;
	walletBalances: {
		currentBalance: number;
		availableBalance: number;
		pendingBalance: number;
	};
	bucketSumBalances: {
		totalCurrentBalance: number;
		totalAvailableBalance: number;
		totalPendingBalance: number;
		bucketCount: number;
	};
	ledgerCalculatedBalance: number;
	/** Empty when consistent; otherwise one sentence per mathematical divergence. */
	discrepancies: string[];
	reconciledAt: string;
}

const adminWalletsApiSlice = walletApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Paged wallet search.
		 *
		 * <p>A `query` rather than a `mutation`, unlike the POST-bodied `/coupons/list` beside it.
		 * The method is POST only because the filter set is too wide for a query string — this is a
		 * read, and typing it as a mutation would forfeit `providesTags`, which is the only thing
		 * that makes freeze / unfreeze / adjust refresh the list the admin is looking at.</p>
		 */
		searchAdminWallets: builder.query<IWalletPagedResult<IAdminWalletListItem>, IAdminWalletSearchRequest>({
			query: (body) => ({ url: '/admin/wallets/search', method: 'POST', body }),
			providesTags: ['AdminWalletList'],
		}),

		/** Full wallet: buckets, recent transactions, and the ledger consistency check. */
		getAdminWalletDetails: builder.query<IAdminWalletDetailsResult, string>({
			query: (walletId) => ({ url: `/admin/wallets/${walletId}` }),
			providesTags: (_result, _error, walletId) => [{ type: 'AdminWallet' as const, id: walletId }],
		}),

		freezeAdminWallet: builder.mutation<IAdminWalletStatusResult, IAdminWalletStatusRequest>({
			query: ({ walletId, ...body }) => ({ url: `/admin/wallets/${walletId}/freeze`, method: 'POST', body }),
			invalidatesTags: (_result, _error, { walletId }) => [{ type: 'AdminWallet' as const, id: walletId }, 'AdminWalletList'],
		}),

		unfreezeAdminWallet: builder.mutation<IAdminWalletStatusResult, IAdminWalletStatusRequest>({
			query: ({ walletId, ...body }) => ({ url: `/admin/wallets/${walletId}/unfreeze`, method: 'POST', body }),
			invalidatesTags: (_result, _error, { walletId }) => [{ type: 'AdminWallet' as const, id: walletId }, 'AdminWalletList'],
		}),

		/**
		 * Credit or debit a wallet, auditably.
		 *
		 * <p>Also invalidates `WalletBalance`: the adjusted wallet may be the signed-in user's own,
		 * and the nav-bar pill reads that tag. Leaving it out makes the header keep reporting the
		 * pre-adjustment figure until its 2-minute poll comes round.</p>
		 */
		adjustAdminWalletBalance: builder.mutation<IAdjustWalletBalanceResult, IAdjustWalletBalanceRequest>({
			query: ({ walletId, ...body }) => ({ url: `/admin/wallets/${walletId}/adjust`, method: 'POST', body }),
			invalidatesTags: (_result, _error, { walletId }) => [
				{ type: 'AdminWallet' as const, id: walletId },
				'AdminWalletList',
				'WalletBalance',
			],
		}),

		/**
		 * Record an over-the-counter deposit (cash or bank-counter transfer) as a wallet credit.
		 *
		 * <p>Invalidates the same tags as `adjust`, for the same reason: the credited wallet may
		 * be the signed-in admin's own, and the nav-bar pill reads `WalletBalance`.</p>
		 */
		manualCreditAdminWallet: builder.mutation<IManualWalletCreditResult, IManualWalletCreditRequest>({
			query: ({ walletId, ...body }) => ({ url: `/admin/wallets/${walletId}/manual-credit`, method: 'POST', body }),
			invalidatesTags: (_result, _error, { walletId }) => [
				{ type: 'AdminWallet' as const, id: walletId },
				'AdminWalletList',
				'WalletBalance',
			],
		}),

		/**
		 * Live reconciliation of stored balances against bucket sums and the ledger.
		 *
		 * <p>`keepUnusedDataFor: 0` on purpose. The result is a MEASUREMENT stamped with
		 * `reconciledAt`, so a cached copy renders an old timestamp as though the check had just
		 * run — which is the one thing a reconciliation screen must never do. Every mount re-runs
		 * it. Deliberately provides no tag: nothing should be able to mark a past measurement
		 * stale, and nothing should silently re-run a check against someone's money.</p>
		 */
		reconcileAdminWallet: builder.query<IWalletReconciliationResult, string>({
			query: (walletId) => ({ url: `/admin/wallets/${walletId}/reconcile` }),
			keepUnusedDataFor: 0,
		}),
	}),
});

export const {
	useSearchAdminWalletsQuery,
	useLazySearchAdminWalletsQuery,
	useGetAdminWalletDetailsQuery,
	useLazyGetAdminWalletDetailsQuery,
	useFreezeAdminWalletMutation,
	useUnfreezeAdminWalletMutation,
	useAdjustAdminWalletBalanceMutation,
	useManualCreditAdminWalletMutation,
	useReconcileAdminWalletQuery,
	useLazyReconcileAdminWalletQuery,
} = adminWalletsApiSlice;
